import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { NavLink, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  deleteProjet,
  getProjetbyMission,
} from '../../redux/projets/projetActions'
import { AppDispatch, RootState, useAppDispatch } from '../../redux/store'
import { formatTitle } from '../../utils/formatTitle'
import { Projet } from '../../types/projetTypes'
import {
  DetailEntityPanel,
  DetailTable,
} from '../../components/common/detail'
import {
  canManageMission,
  DetailRowActions,
} from '../../components/common/detail/DetailRowActions'
import {
  ListPageDateCell,
  ListPagePill,
  PRIORITY_PILL,
  STATUS_PILL,
} from '../../components/common/ListPageCells'
import { ListPageDescription } from '../../components/common/ListPageDescription'
import { priorityLabelsTranslations } from '../../utils/constants'
import '../../components/common/ListPageTable.css'
import i18n from '../../i18n'

type Props = {
  IdMission: string
  onCountChange?: (count: number) => void
}

const PAGE_SIZE = 100

const ProjetListByMission: React.FC<Props> = ({ IdMission, onCountChange }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch: AppDispatch = useAppDispatch()
  const roles = useSelector((state: any) => state.auth.roles)
  const loading = useSelector((state: RootState) => state.projet.loading)
  const response = useSelector(
    (state: RootState) => state.projet.ProjetResponse
  )
  const [search, setSearch] = useState('')

  useEffect(() => {
    if (IdMission) {
      dispatch(
        getProjetbyMission({
          MissionId: IdMission,
          page: 1,
          pageSize: PAGE_SIZE,
        })
      )
    }
  }, [dispatch, IdMission])

  const rows = useMemo(() => {
    const list = response?.projets ?? []
    const q = search.trim().toLowerCase()
    if (!q) return list
    return list.filter((p) =>
      (p.ProjetName || '').toLowerCase().includes(q)
    )
  }, [response?.projets, search])

  useEffect(() => {
    onCountChange?.(response?.total ?? rows.length)
  }, [onCountChange, response?.total, rows.length])

  const canAdd = canManageMission(roles)

  return (
    <DetailEntityPanel
      listTitle={t('missionDetailsPage.sections.projectList', {
        defaultValue: 'Project list',
      })}
      searchPlaceholder={t('filterByName', { defaultValue: 'Filter by name' })}
      searchValue={search}
      onSearchChange={setSearch}
      addLabel={t('buttons.addProject', { defaultValue: 'Add New Project' })}
      onAdd={() => navigate(`/mission/${IdMission}/addprojet`)}
      canAdd={canAdd}
    >
      <DetailTable<Projet>
        loading={loading && !response}
        loadingLabel={t('loading')}
        emptyTitle={t('missionDetailsPage.empty.title', {
          defaultValue: 'Nothing here yet',
        })}
        emptyMessage={t('missionDetailsPage.empty.projects', {
          defaultValue:
            'No project is attached to this mission yet. Create one, or attach an existing project from a pipeline.',
        })}
        rows={rows}
        rowKey={(r) => r._id as string}
        columns={[
          {
            key: 'name',
            header: t('Project Name', { defaultValue: 'Project name' }),
            render: (r) => (
              <NavLink
                to={`/mission/${IdMission}/projet/${r._id}`}
                state={{
                  missionId: IdMission,
                  projectId: r._id,
                  projectLabel: r.ProjetName
                    ? formatTitle(r.ProjetName)
                    : undefined,
                }}
                className="incyia-name-link"
              >
                <ListPageDescription
                  value={
                    r.ProjetName ? formatTitle(r.ProjetName) : undefined
                  }
                  tone="primary"
                />
              </NavLink>
            ),
          },
          {
            key: 'priority',
            header: t('Priority'),
            render: (r) => {
              const priority = Number(r.Priority)
              const style = PRIORITY_PILL[priority] ?? PRIORITY_PILL[4]
              return (
                <ListPagePill
                  label={
                    t(`priority.${priority}`, {
                      defaultValue:
                        priorityLabelsTranslations[priority] || style.label,
                    }) || style.label
                  }
                  bg={style.bg}
                  color={style.color}
                />
              )
            },
          },
          {
            key: 'status',
            header: t('Status'),
            render: (r) => {
              const style = STATUS_PILL[r.Status || ''] ?? {
                bg: '#EAF0F0',
                color: '#5F7484',
              }
              return (
                <ListPagePill
                  label={t(r.Status || '', {
                    defaultValue: r.Status || '—',
                  })}
                  bg={style.bg}
                  color={style.color}
                />
              )
            },
          },
          {
            key: 'end',
            header: t('End Date', { defaultValue: 'End date' }),
            render: (r) => (
              <ListPageDateCell value={r.DateFin} locale={i18n.language} />
            ),
          },
        ]}
        actionsHeader={t('Actions', { defaultValue: 'Action' })}
        renderActions={(r) => (
          <DetailRowActions
            roles={roles}
            onEdit={() => navigate(`/projet/${r._id}`)}
            onDelete={() => dispatch(deleteProjet(r._id as string))}
            deleteItem={{
              entityType: t('projects', { defaultValue: 'Project' }),
              name: r.ProjetName ? formatTitle(r.ProjetName) : undefined,
              details: [
                {
                  label: t('deleteConfirm.fields.mission'),
                  value: r.MissionName
                    ? formatTitle(r.MissionName)
                    : '—',
                },
                {
                  label: t('deleteConfirm.fields.status'),
                  value: r.Status || '—',
                },
              ],
            }}
          />
        )}
      />
    </DetailEntityPanel>
  )
}

export default ProjetListByMission
